import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { loadBuildingsForSelector } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  getBuildingSelectProps,
  needsBuildingSelection,
  shouldShowBuildingSelector,
  selectBuilding,
} from '../../../utils/buildingSelection';
import { incomeExpenseApi, ProjectBankAccountActionLog } from '../../../api/incomeExpenseApi';
import { BulkListPagination } from '../../../components/bulk-fee/BulkListPagination';
import { HISTORY_PAGE_SIZE } from '../../../api/paginatedHistory';
import { useLang } from '../../../contexts/LangContext';

const formatAction = (
  action: ProjectBankAccountActionLog['action'],
  accountType?: string | null,
) => {
  if (accountType === 'Phiếu thu' || accountType === 'Phiếu chi') {
    if (action === 'DELETE') return 'Huỷ phiếu';
    if (action === 'UPDATE') return 'Chỉnh sửa phiếu';
  }
  switch (action) {
    case 'CREATE':
      return 'Tạo mới';
    case 'UPDATE':
      return 'Cập nhật';
    case 'DELETE':
      return 'Xóa';
    default:
      return action;
  }
};

const getActionBadgeClass = (action: ProjectBankAccountActionLog['action']) => {
  switch (action) {
    case 'CREATE':
      return 'bg-emerald-100 text-emerald-700';
    case 'UPDATE':
      return 'bg-cyan-100 text-cyan-700';
    case 'DELETE':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export const ProjectBankAccountHistoryTab: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
  const [buildingId, setBuildingId] = useState('');
  const [logs, setLogs] = useState<ProjectBankAccountActionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    });
  }, [user]);

  const loadLogs = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const result = await incomeExpenseApi.getProjectBankAccountActionLogs(buildingId, {
        page,
        limit: HISTORY_PAGE_SIZE,
      });
      setLogs(result.data);
      setTotal(result.meta.total);
      setTotalPages(result.meta.totalPages);
    } catch {
      toast.error('Không thể tải lịch sử thao tác');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId, page]);

  useEffect(() => {
    setPage(1);
  }, [buildingId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <div className="space-y-4">
      {shouldShowBuildingSelector(buildings) && (
        <select
          value={buildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
          {...getBuildingSelectProps(buildings)}
          className="rounded-lg border px-3 py-2 text-sm"
        >
          {needsBuildingSelection(buildings) && <option value="">-- Chọn tòa nhà --</option>}
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      )}

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Thời gian</th>
              <th className="px-4 py-3 text-left">Thao tác</th>
              <th className="px-4 py-3 text-left">Tên tài khoản</th>
              <th className="px-4 py-3 text-left">Loại tài khoản</th>
              <th className="px-4 py-3 text-left">Chi tiết</th>
              <th className="px-4 py-3 text-left">Người thực hiện</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Đang tải...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Chưa có lịch sử</td></tr>
            ) : logs.map((log) => (
              <tr key={log.id} className="border-t">
                <td className="px-4 py-3 whitespace-nowrap">
                  {new Date(log.createdAt).toLocaleString('vi-VN')}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getActionBadgeClass(log.action)}`}>
                    {formatAction(log.action, log.accountType)}
                  </span>
                </td>
                <td className="px-4 py-3">{log.accountName || '—'}</td>
                <td className="px-4 py-3">{log.accountType || '—'}</td>
                <td className="px-4 py-3">{log.notes || '—'}</td>
                <td className="px-4 py-3">{log.creator?.name || log.creator?.email || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-4">
          <BulkListPagination
            page={page}
            totalPages={totalPages}
            total={total}
            onPageChange={setPage}
            t={t}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectBankAccountHistoryTab;
